/**
 * Compliance Archive — read-only operational archive.
 * Same Compliance Case ID (CMP-######) and Compliance Decision ID.
 * No separate Archive ID.
 * Final Outcome: Approved | Rejected.
 * Transferred cases have no Archive entry.
 */

import {
  CASE_TYPES,
  COMPLIANCE_TYPES,
  FINAL_OUTCOME_OPTIONS,
  SUPPORT_TEAM_MEMBERS,
  decisionIdFromCaseId,
  display,
  formatAssignedDisplay,
  formatComplianceDateTime,
} from './complianceQueueSpec';

export const COMPLIANCE_ARCHIVE_ROUTE = '/admin/verification/archive';

const RECEIVED = '2026-03-03T09:00:00+10:00';
const ARCHIVE_SAMPLE_DECIDED_BY = 'Jordan Lee';

const cloneCase = (row) => JSON.parse(JSON.stringify(row));

const parseSampleDate = (day, monthShort, year, hour, minute, isPm) => {
  const months = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  };
  let h = hour;
  if (isPm && h < 12) h += 12;
  if (!isPm && h === 12) h = 0;
  return new Date(year, months[monthShort], day, h, minute, 0, 0).toISOString();
};

export const formatComplianceArchiveDateTime = (value) => formatComplianceDateTime(value);

/** Spec: retain the same Case ID — no separate Archive ID. */
export const archiveIdFromCaseId = (caseId) => {
  const raw = String(caseId || '').trim();
  if (!raw || raw === '—') return raw;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;
  return `CMP-${digits.padStart(6, '0')}`;
};

const retainedRecordStatus = (queueCase, finalOutcome) => {
  if (queueCase.recordStatus != null && queueCase.recordStatus !== '') {
    return queueCase.recordStatus === 'No Record' ? '—' : queueCase.recordStatus;
  }
  if (finalOutcome === 'Rejected') {
    const status =
      queueCase.businessRecordOutcome?.recordStatus ||
      queueCase.currentRecord?.recordStatus ||
      null;
    if (!status || status === 'No Record') return '—';
    return status;
  }
  return (
    queueCase.businessRecordOutcome?.recordStatus ||
    queueCase.currentRecord?.recordStatus ||
    'Active'
  );
};

export const toArchivedCase = (queueCase, { handoffOn } = {}) => {
  const caseId = archiveIdFromCaseId(queueCase.compliance_id || queueCase.caseId || queueCase.complianceCaseId);
  const when = handoffOn || queueCase.finalisedOn || queueCase.handoffOn || RECEIVED;
  const decisionId =
    queueCase.complianceDecisionId ||
    queueCase.decisionId ||
    queueCase.decision?.decisionId ||
    decisionIdFromCaseId(caseId);
  const finalOutcome =
    queueCase.finalDecision?.outcome ||
    queueCase.finalOutcome ||
    queueCase.finalDecision ||
    'Approved';
  const supportTeamMember = formatAssignedDisplay(
    queueCase.support_team_member ||
      queueCase.supportTeamMember ||
      queueCase.assigned_to ||
      queueCase.finalDecision?.decidedBy ||
      ARCHIVE_SAMPLE_DECIDED_BY
  );
  const alreadyHasHandoff = (queueCase.auditLog || []).some(
    (entry) =>
      entry.event === 'Archive handoff' ||
      entry.event === 'Moved to Archive' ||
      entry.event === 'Archive Handoff'
  );

  return {
    ...cloneCase(queueCase),
    id: caseId,
    complianceCaseId: caseId,
    compliance_id: caseId,
    caseId,
    queueCaseId: caseId,
    archiveId: caseId,
    status: 'Decided',
    case_status: 'Decided',
    caseType: queueCase.case_type || queueCase.caseType || 'New',
    case_type: queueCase.case_type || queueCase.caseType || 'New',
    complianceType: queueCase.compliance_type || queueCase.complianceType || queueCase.snapshot?.complianceType,
    complianceItem: queueCase.compliance_item || queueCase.complianceItem || queueCase.snapshot?.complianceItem,
    complianceItemId: queueCase.compliance_item_id || queueCase.complianceItemId || queueCase.snapshot?.sourceId,
    tradeCategory: queueCase.trade_category || queueCase.tradeCategory || queueCase.snapshot?.tradeCategory,
    businessName: queueCase.business_name || queueCase.businessName,
    businessId: queueCase.business_id || queueCase.businessId,
    businessAvatar: queueCase.business_avatar || queueCase.businessAvatar,
    supportTeamMember: supportTeamMember === 'Unassigned' ? ARCHIVE_SAMPLE_DECIDED_BY : supportTeamMember,
    finalOutcome: finalOutcome === 'Transfer' ? null : finalOutcome,
    finalDecision: finalOutcome === 'Transfer' ? null : finalOutcome,
    recordStatus: retainedRecordStatus(queueCase, finalOutcome),
    decisionId,
    complianceDecisionId: decisionId,
    handoffOn: when,
    finalisedOn: queueCase.finalDecision?.decidedOn || queueCase.finalisedOn || when,
    submittedAt: queueCase.submitted_at || queueCase.submittedAt,
    auditLog: alreadyHasHandoff
      ? cloneCase(queueCase.auditLog || [])
      : [
          ...(queueCase.auditLog || []),
          {
            dateTime: when,
            event: 'Moved to Archive',
            details: `System; Lifecycle Control; ${caseId}`,
            relatedRecord: caseId,
            record: caseId,
          },
        ],
  };
};

const archivedSample = ({
  complianceCaseId,
  businessName,
  businessId,
  businessAvatar,
  complianceType,
  complianceItem,
  complianceItemId,
  tradeCategory,
  businessTradeCategory,
  caseType,
  supportTeamMember,
  finalOutcome,
  recordStatus,
  decisionId,
  finalisedOn,
  submittedAt,
}) =>
  toArchivedCase(
    {
      compliance_id: complianceCaseId,
      business_name: businessName,
      business_id: businessId,
      business_avatar: businessAvatar,
      compliance_type: complianceType,
      compliance_item: complianceItem,
      compliance_item_id: complianceItemId,
      trade_category: tradeCategory,
      business_trade_category: businessTradeCategory || tradeCategory,
      case_type: caseType,
      support_team_member: supportTeamMember || ARCHIVE_SAMPLE_DECIDED_BY,
      case_status: 'Decided',
      submitted_at: submittedAt || RECEIVED,
      last_updated: finalisedOn || RECEIVED,
      finalisedOn: finalisedOn || RECEIVED,
      recordStatus,
      decisionId: decisionId || decisionIdFromCaseId(complianceCaseId),
      finalDecision: {
        outcome: finalOutcome,
        decidedBy: supportTeamMember || ARCHIVE_SAMPLE_DECIDED_BY,
        decidedOn: finalisedOn || RECEIVED,
      },
      businessRecordOutcome: {
        recordStatus: recordStatus === '—' ? 'No Record' : recordStatus,
      },
      auditLog: [
        {
          dateTime: submittedAt || RECEIVED,
          event: 'Case received',
          details: `System created ${complianceCaseId} as New.`,
          relatedRecord: '—',
        },
        {
          dateTime: finalisedOn || RECEIVED,
          event: 'Final decision',
          details: `${supportTeamMember || ARCHIVE_SAMPLE_DECIDED_BY} recorded ${finalOutcome} (${
            decisionId || decisionIdFromCaseId(complianceCaseId)
          }).`,
          relatedRecord: decisionId || decisionIdFromCaseId(complianceCaseId),
        },
      ],
    },
    { handoffOn: finalisedOn || RECEIVED }
  );

const APPLIED_RULE_BY_SOURCE_ID = {
  'LIC-0002': 'COM-0001',
  'LIC-0001': 'COM-0002',
  'BDG-0001': 'COM-0003',
  'TKT-0001': 'COM-0004',
};

const COMPLIANCE_ITEMS_BY_TYPE = {
  Licence: [
    { id: 'LIC-0001', name: 'Electrical Licence', tradeCategory: 'Electrical' },
    { id: 'LIC-0002', name: 'Plumbing Licence', tradeCategory: 'Plumbing' },
    { id: 'LIC-0003', name: 'Gas Fitting Licence', tradeCategory: 'Plumbing' },
  ],
  Badge: [
    { id: 'BDG-0001', name: 'First Aid', tradeCategory: 'All Trades' },
    { id: 'BDG-0002', name: 'Industry Membership', tradeCategory: 'All Trades' },
    { id: 'BDG-0003', name: 'Working with Children Check', tradeCategory: 'All Trades' },
  ],
  Ticket: [
    { id: 'TKT-0001', name: 'White Card', tradeCategory: 'All Trades' },
    { id: 'TKT-0002', name: 'Working at Heights', tradeCategory: 'All Trades' },
    { id: 'TKT-0003', name: 'Confined Spaces', tradeCategory: 'All Trades' },
  ],
};

const TRADE_CATEGORIES = [
  'Plumbing',
  'Electrical',
  'Building',
  'Gas Fitting',
  'Carpentry',
  'Roofing',
  'Painting',
  'Landscaping',
];

let archiveState = [
  archivedSample({
    complianceCaseId: 'CMP-004180',
    businessName: 'Apex Plumbing',
    businessId: 'BUS-001284',
    businessAvatar: 'https://i.pravatar.cc/150?img=12',
    complianceType: 'Licence',
    complianceItem: 'Plumbing Licence',
    complianceItemId: 'LIC-0002',
    tradeCategory: 'Plumbing',
    businessTradeCategory: 'Plumbing',
    caseType: 'Renewal',
    supportTeamMember: 'Jordan Lee',
    finalOutcome: 'Approved',
    recordStatus: 'Active',
    decisionId: 'CMP-DEC-4180',
    finalisedOn: parseSampleDate(15, 'Mar', 2026, 2, 20, true),
    submittedAt: parseSampleDate(3, 'Mar', 2026, 9, 0, false),
  }),
  archivedSample({
    complianceCaseId: 'CMP-004185',
    businessName: 'Bright Spark Electrical',
    businessId: 'BUS-001285',
    businessAvatar: 'https://i.pravatar.cc/150?img=33',
    complianceType: 'Ticket',
    complianceItem: 'White Card',
    complianceItemId: 'TKT-0001',
    tradeCategory: 'All Trades',
    businessTradeCategory: 'Electrical',
    caseType: 'New',
    supportTeamMember: 'Mia Chen',
    finalOutcome: 'Rejected',
    recordStatus: '—',
    decisionId: 'CMP-DEC-4185',
    finalisedOn: parseSampleDate(18, 'Mar', 2026, 12, 0, false),
    submittedAt: parseSampleDate(10, 'Mar', 2026, 9, 18, false),
  }),
  archivedSample({
    complianceCaseId: 'CMP-004182',
    businessName: 'Metro Building Group',
    businessId: 'BUS-001287',
    businessAvatar: 'https://i.pravatar.cc/150?img=44',
    complianceType: 'Licence',
    complianceItem: 'Electrical Licence',
    complianceItemId: 'LIC-0001',
    tradeCategory: 'Electrical',
    businessTradeCategory: 'Building',
    caseType: 'Replacement',
    supportTeamMember: 'Jordan Lee',
    finalOutcome: 'Approved',
    recordStatus: 'Active',
    decisionId: 'CMP-DEC-4182',
    finalisedOn: parseSampleDate(20, 'Mar', 2026, 4, 30, true),
    submittedAt: parseSampleDate(12, 'Mar', 2026, 2, 35, true),
  }),
  archivedSample({
    complianceCaseId: 'CMP-003983',
    businessName: 'Urban Gas Works',
    businessId: 'BUS-001286',
    businessAvatar: 'https://i.pravatar.cc/150?img=22',
    complianceType: 'Badge',
    complianceItem: 'First Aid',
    complianceItemId: 'BDG-0001',
    tradeCategory: 'All Trades',
    businessTradeCategory: 'Plumbing',
    caseType: 'New',
    supportTeamMember: 'Jordan Lee',
    finalOutcome: 'Approved',
    recordStatus: 'Active',
    decisionId: 'CMP-DEC-3983',
    finalisedOn: parseSampleDate(2, 'Apr', 2025, 10, 24, false),
    submittedAt: parseSampleDate(28, 'Mar', 2025, 9, 0, false),
  }),
];

const businessNames = [
  'Northside Plumbing Co.',
  'Bolt Electrical Services',
  'Coastal Gas & Heating',
  'Modern Painters Collective',
  'Master Builders Guild',
  'Green Lawn Care Pty Ltd',
  'Smart Locksmith Co.',
  'Elite Flooring Professionals',
  'QuickFix Tiling & Reno',
  'Rapid Roofing Group',
];

for (let i = 0; i < 24; i++) {
  const n = 3900 + i;
  const complianceType = COMPLIANCE_TYPES[i % COMPLIANCE_TYPES.length];
  const caseType = CASE_TYPES[i % CASE_TYPES.length];
  const items = COMPLIANCE_ITEMS_BY_TYPE[complianceType];
  const item = items[i % items.length];
  const businessTradeCategory = TRADE_CATEGORIES[i % TRADE_CATEGORIES.length];
  const rejected = i % 3 === 0;
  const keepCurrent = rejected && caseType !== 'New';
  archiveState.push(
    archivedSample({
      complianceCaseId: `CMP-${String(n).padStart(6, '0')}`,
      businessName: businessNames[i % businessNames.length],
      businessId: `BUS-${String(300 + i).padStart(6, '0')}`,
      businessAvatar: `https://i.pravatar.cc/150?img=${(i % 70) + 5}`,
      complianceType,
      complianceItem: item.name,
      complianceItemId: item.id,
      appliedRuleId: APPLIED_RULE_BY_SOURCE_ID[item.id] || null,
      tradeCategory: item.tradeCategory || businessTradeCategory,
      businessTradeCategory,
      caseType,
      supportTeamMember: SUPPORT_TEAM_MEMBERS[i % SUPPORT_TEAM_MEMBERS.length],
      finalOutcome: rejected ? 'Rejected' : 'Approved',
      recordStatus: rejected && !keepCurrent ? '—' : 'Active',
      decisionId: decisionIdFromCaseId(`CMP-${String(n).padStart(6, '0')}`),
      finalisedOn: parseSampleDate(4 + (i % 20), 'Mar', 2026, 9 + (i % 8), (i * 7) % 60, i % 2 === 0),
      submittedAt: parseSampleDate(1 + (i % 12), 'Mar', 2026, 8 + (i % 6), (i * 11) % 60, false),
    })
  );
}

export const collectArchiveCases = () =>
  archiveState
    .filter((row) => row.finalOutcome === 'Approved' || row.finalOutcome === 'Rejected')
    .map(cloneCase)
    .sort(
      (a, b) =>
        new Date(b.finalisedOn || 0).getTime() - new Date(a.finalisedOn || 0).getTime()
    );

export const findArchiveCase = (id) => {
  if (!id) return null;
  const digits = String(id).replace(/\D/g, '');
  const found = archiveState.find((row) => {
    const caseDigits = String(row.complianceCaseId || row.caseId || '').replace(/\D/g, '');
    return caseDigits === digits || String(row.complianceCaseId) === String(id);
  });
  return found ? cloneCase(found) : null;
};

export const getArchivedComplianceCaseIdSet = () => {
  const ids = new Set();
  archiveState.forEach((row) => {
    const n = String(row.complianceCaseId || row.caseId || '').replace(/\D/g, '');
    if (n) ids.add(n);
  });
  return ids;
};

export const handoffQueueCaseToArchive = (queueCase) => {
  const caseId = queueCase?.compliance_id || queueCase?.caseId || queueCase?.complianceCaseId;
  if (!caseId) return null;
  const outcome = queueCase.finalDecision?.outcome || queueCase.finalOutcome;
  if (outcome === 'Transfer') return null;
  const digits = String(caseId).replace(/\D/g, '');
  const existing = archiveState.find(
    (row) => String(row.complianceCaseId || '').replace(/\D/g, '') === digits
  );
  if (existing) return cloneCase(existing);
  const archived = toArchivedCase(queueCase, { handoffOn: new Date().toISOString() });
  if (!archived.finalOutcome) return null;
  archiveState.push(archived);
  return cloneCase(archived);
};

export const matchesArchiveSearch = (row, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.complianceCaseId,
    row.caseId,
    row.decisionId || row.complianceDecisionId,
    row.businessName,
    row.businessId,
    row.complianceItem,
    row.complianceItemId,
    row.complianceType,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

export const ARCHIVE_FINAL_OUTCOME_OPTIONS = [
  { value: 'All', label: 'All' },
  ...FINAL_OUTCOME_OPTIONS.map((value) => ({ value, label: value })),
];

export const ARCHIVE_FINALISED_DATE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Last 7 Days', label: 'Last 7 Days' },
  { value: 'Last 30 Days', label: 'Last 30 Days' },
  { value: 'Last 90 Days', label: 'Last 90 Days' },
];

export const matchesFinalisedDate = (row, filters = {}) => {
  if (!filters.dateRange || filters.dateRange === 'All') return true;
  const finalised = new Date(row.finalisedOn || row.handoffOn);
  if (Number.isNaN(finalised.getTime())) return false;

  const now = new Date();
  now.setHours(23, 59, 59, 999);
  const days =
    filters.dateRange === 'Last 7 Days'
      ? 7
      : filters.dateRange === 'Last 30 Days'
        ? 30
        : filters.dateRange === 'Last 90 Days'
          ? 90
          : null;
  if (!days) return true;
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - days);
  cutoff.setHours(0, 0, 0, 0);
  return finalised >= cutoff && finalised <= now;
};

export const matchesArchiveFilters = (row, filters = {}) => {
  if (filters.type && filters.type !== 'All' && row.complianceType !== filters.type) return false;
  if (filters.finalOutcome && filters.finalOutcome !== 'All') {
    if (String(row.finalOutcome || '') !== filters.finalOutcome) return false;
  }
  if (!matchesFinalisedDate(row, filters)) return false;
  return true;
};

export const getArchiveTypeFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...COMPLIANCE_TYPES.map((value) => ({ value, label: value })),
];

export const archiveListRow = (row) => ({
  id: row.complianceCaseId || row.caseId,
  complianceCaseId: row.complianceCaseId || row.caseId,
  businessName: row.businessName || '—',
  businessAvatar: row.businessAvatar,
  complianceType: row.complianceType || '—',
  complianceItem: row.complianceItem || '—',
  caseType: row.caseType || '—',
  finalOutcome: row.finalOutcome || '—',
  recordStatus: row.recordStatus === 'No Record' ? '—' : display(row.recordStatus),
  decisionId: row.decisionId || row.complianceDecisionId || '—',
  finalisedOn: row.finalisedOn,
});
